import React, { useEffect } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { usePermissions } from '@billda/permissions-ui';
import { useShellStore } from '../../../stores/shellStore';
import { useNavigationHandler } from '../../../hooks/useNavigationHandler';
import { useCommandPalette } from '../../../../features/search/providers/CommandPaletteProvider';
import { MobileSidebar } from './MobileSidebar';
import { DesktopSidebar } from './DesktopSidebar';

export const ExpandedSidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const permissions = usePermissions();
  const handleNavigation = useNavigationHandler();
  const { openPalette } = useCommandPalette();

  const {
    sidebarOpen,
    closeSidebar,
    sidebarCollapsed,
    toggleDesktopSidebar,
    collapsedGroups,
    toggleGroupCollapsed,
  } = useShellStore(
    useShallow(state => ({
      sidebarOpen: state.sidebarOpen,
      closeSidebar: state.closeSidebar,
      sidebarCollapsed: state.sidebarCollapsed,
      toggleDesktopSidebar: state.toggleDesktopSidebar,
      collapsedGroups: state.collapsedGroups,
      toggleGroupCollapsed: state.toggleGroupCollapsed,
    }))
  );

  // Auto-close on route change — ONLY in mobile overlay mode
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      closeSidebar();
    }
    // Only react to location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = (itemPath: string) => location.pathname === itemPath;

  if (isMobile) {
    return (
      <MobileSidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        permissions={permissions}
        isActive={isActive}
        collapsedGroups={collapsedGroups}
        onToggleCollapsed={toggleGroupCollapsed}
        onNavigate={handleNavigation}
        onSearch={openPalette}
      />
    );
  }

  if (sidebarCollapsed) return null;

  return (
    <DesktopSidebar
      onToggleCollapse={toggleDesktopSidebar}
      permissions={permissions}
      isActive={isActive}
      collapsedGroups={collapsedGroups}
      onToggleCollapsed={toggleGroupCollapsed}
      onNavigate={handleNavigation}
      onSearch={openPalette}
    />
  );
};
